import React from "react";
import { 
  Star, 
  Instagram, 
  Facebook, 
  Globe,
  Link2,
  MapPin,
  ExternalLink,
  MapPinIcon,
  Phone,
  Globe2,
  MessageCircle,
  Palette,
  Check,
  ClipboardList,
  CalendarCheck,
  ShieldAlert,
  Ticket,
  ShoppingCart,
  Link as LinkIcon,
  PlayCircle
} from "lucide-react";

const customIconsList = [
  { id: 'menu', icon: ClipboardList, label: 'Carta', tKey: 'todo_en_1.icon_menu', color: '#F97316' },
  { id: 'calendar', icon: CalendarCheck, label: 'Reservar', tKey: 'todo_en_1.icon_calendar', color: '#10B981' },
  { id: 'allergens', icon: ShieldAlert, label: 'Alérgenos', tKey: 'todo_en_1.icon_allergens', color: '#F59E0B' },
  { id: 'offer', icon: Ticket, label: 'Oferta', tKey: 'todo_en_1.icon_offer', color: '#EC4899' },
  { id: 'shop', icon: ShoppingCart, label: 'Comprar', tKey: 'todo_en_1.icon_shop', color: '#3B82F6' },
  { id: 'link', icon: LinkIcon, label: 'Enlace', tKey: 'todo_en_1.icon_link', color: '#6B7280' },
  { id: 'youtube', icon: PlayCircle, label: 'Youtube', tKey: 'todo_en_1.icon_youtube', color: '#EF4444' },
  { id: 'event', icon: Star, label: 'Evento', tKey: 'todo_en_1.icon_event', color: '#8B5CF6' },
];
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/components/LanguageContext";

export const getBackgroundColors = (t) => [
  { id: 1, name: t('todo_en_1.theme_orange') || "Naranja Tapstar", from: "#feae2c", via: "#F5DEB3", to: "#FFFDD0" },
  { id: 2, name: t('todo_en_1.theme_white') || "Blanco", from: "#E8E8E8", via: "#F4F4F4", to: "#FFFFFF" },
  { id: 3, name: t('todo_en_1.theme_black') || "Negro", from: "#0A0A0A", via: "#2C2C2C", to: "#6B6B6B" },
  { id: 4, name: t('todo_en_1.theme_sand') || "Arena", from: "#C8956C", via: "#E8C99A", to: "#FAF0E0" },
  { id: 5, name: t('todo_en_1.theme_anthracite') || "Antracita", from: "#2D3748", via: "#5A6B82", to: "#C8D4E0" },
  { id: 6, name: t('todo_en_1.theme_terracotta') || "Terracota", from: "#B83A10", via: "#E8A882", to: "#FDF0E8" },
  { id: 7, name: t('todo_en_1.theme_pink') || "Rosa", from: "#D97FA0", via: "#F4C0D1", to: "#FEF0F4" },
  { id: 8, name: t('todo_en_1.theme_lilac') || "Lila", from: "#7840B8", via: "#C4A0E8", to: "#F0E8FC" },
  { id: 9, name: t('todo_en_1.theme_navy') || "Azul marino", from: "#1A3060", via: "#6090D0", to: "#E0EEFA" },
  { id: 10, name: t('todo_en_1.theme_green') || "Verde", from: "#1A6040", via: "#5DAF85", to: "#E0F5EC" },
  { id: 11, name: t('todo_en_1.theme_gold') || "Dorado", from: "#9A6010", via: "#D4A855", to: "#FDF5DC" },
  { id: 12, name: t('todo_en_1.theme_violet') || "Violeta", from: "#4A10D0", via: "#A878F0", to: "#EDE0FF" }
];

const linkIcons = {
  google_review: { icon: Star, color: "bg-[#4285F4]", logo: "https://cdn.simpleicons.org/google/white", imgClass: "w-4 h-4 sm:w-5 sm:h-5" },
  instagram: { icon: Instagram, color: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500" },
  tiktok: { icon: Globe, color: "bg-[#010101]", logo: "https://cdn.simpleicons.org/tiktok/white", imgClass: "w-4 h-4 sm:w-5 sm:h-5" },
  facebook: { icon: Facebook, color: "bg-blue-600" },
  tripadvisor: { icon: MapPin, color: "bg-[#34E0A1]", logo: "https://cdn.simpleicons.org/tripadvisor/white" },
  website: { icon: Globe, color: "bg-indigo-600" },
  whatsapp: { icon: MessageCircle, color: "bg-green-500", logo: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" },
  custom: { icon: Link2, color: "bg-slate-600" },
};

export default function PreviewPanel({ data, fullSize = false, isEditable = false, onThemeChange }) {
  const { t } = useLanguage();
  const { id, logo_url, business_name, address, phone, website, custom_text, custom_text_color = 'black', links = [], theme } = data;

  const BACKGROUND_COLORS = getBackgroundColors(t);
  const defaultTheme = { from: '#feae2c', via: '#F5DEB3', to: '#FFFDD0' };
  const currentTheme = theme || defaultTheme;

  const activeLinks = links.filter(link => link.enabled);

  const handleLinkClick = (linkType, finalHref) => {
    // Evitar tracking en previsualización de admin si no pasamos ID
    if (id) {
       // Usamos fetch nativo con keepalive para asegurar que la petición 
       // sobreviva al cierre o background de la pestaña.
       try {
         // Extraer el CSRF Token para que Django no bloquee el POST del fetch nativo
         const getCsrfToken = () => {
           const cookies = document.cookie.split(';');
           for (let c of cookies) {
             const trimmed = c.trim();
             if (trimmed.startsWith('csrftoken=')) {
               return decodeURIComponent(trimmed.slice(10));
             }
           }
           return '';
         };

         fetch(`/api/v1/products/${id}/click/`, {
           method: 'POST',
           headers: { 
               'Content-Type': 'application/json',
               'X-CSRFToken': getCsrfToken()
           },
           body: JSON.stringify({ link_type: linkType, url: finalHref }),
           keepalive: true
         });
       } catch (e) {
         console.error("Tracking Error:", e);
       }
    }
  };

  return (
    <div 
      className={`p-6 sm:p-8 ${fullSize ? 'min-h-screen' : 'rounded-2xl min-h-[500px]'}`}
      style={{ background: `linear-gradient(to bottom, ${currentTheme.from}, ${currentTheme.via}, ${currentTheme.to})` }}
    >
      <div className="max-w-sm mx-auto space-y-4">
        {/* Business Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 p-4 sm:p-5">
          {/* Logo */}
          <div className="flex justify-center mb-3 sm:mb-4">
            {logo_url ? (
              <img 
                src={logo_url} 
                alt={business_name}
                className="w-full h-auto max-h-48 object-contain"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-2xl sm:text-3xl font-bold text-slate-400">
                  {business_name?.charAt(0) || "?"}
                </span>
              </div>
            )}
          </div>

          {/* Business Info */}
          <div className="text-left">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 sm:mb-3 px-2">
              {business_name || t('todo_en_1.placeholder_business_name')}
            </h2>

            {address && (
              <div className="flex items-center justify-start gap-1.5 text-[10px] sm:text-xs text-slate-600 mb-1.5 px-2">
                <MapPinIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{address}</span>
              </div>
            )}

            {phone && (
              <div className="flex items-center justify-start gap-1.5 text-[10px] sm:text-xs text-slate-600 mb-1.5 px-2">
                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{phone}</span>
              </div>
            )}

            {website && (
              <div className="flex items-center justify-start gap-1.5 text-[10px] sm:text-xs text-slate-600 mb-1.5 px-2">
                <Globe2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{website}</span>
              </div>
            )}

            {/* Theme / Color Picker Trigger - Inside Card */}
            {isEditable && onThemeChange && (
              <div className="flex justify-end pr-2 pb-1 pt-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <button 
                      type="button" 
                      className="w-8 h-8 bg-slate-100 hover:bg-[#feae2c]/20 rounded-full flex items-center justify-center transition-all border border-slate-200 group"
                      title={t('todo_en_1.change_bg_color')}
                    >
                      <Palette className="w-4 h-4 text-slate-500 group-hover:text-[#feae2c] transition-colors" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[280px] p-4 bg-white rounded-xl shadow-2xl border border-slate-100" align="end">
                    <p className="text-sm font-bold text-slate-800 mb-3">{t('todo_en_1.change_bg_color')}</p>
                    <div className="grid grid-cols-4 gap-2">
                      {BACKGROUND_COLORS.map(color => {
                        const isSelected = currentTheme?.id === color.id || (!theme && color.id === 1);
                        return (
                          <div 
                            key={color.id}
                            onClick={() => onThemeChange(color)}
                            className={`aspect-[1.5/1] rounded-lg cursor-pointer border-2 relative transition-transform hover:scale-105 ${isSelected ? 'border-blue-500' : 'border-transparent'}`}
                            style={{ background: `linear-gradient(to bottom, ${color.from}, ${color.via}, ${color.to})` }}
                            title={color.name}
                          >
                            {isSelected && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-5 h-5 bg-white/90 rounded-full flex items-center justify-center shadow-sm">
                                  <Check className="w-3 h-3 text-blue-500" />
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </div>

        {/* Custom Text */}
        {custom_text && (
          <p className={`text-sm sm:text-base px-2 text-center ${custom_text_color === 'white' ? 'text-white' : 'text-black'}`}>
            {custom_text}
          </p>
        )}

        {/* Links */}
        {activeLinks.length > 0 ? (
          activeLinks.map((link, index) => {
            const linkConfig = linkIcons[link.type] || linkIcons.custom;
            let Icon = linkConfig.icon;
            let customIconObj = null;
            if (link.type === 'custom') {
               customIconObj = customIconsList.find(c => c.id === link.customIcon);
               if (customIconObj) {
                 Icon = customIconObj.icon;
               }
            }
            
            let finalHref = link.url;
            if (link.type === 'instagram') {
               let cleanUsername = (link.url || '').replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/^https?:\/\//, '').replace(/^@/, '').replace(/\/$/, '');
               finalHref = `https://www.instagram.com/${cleanUsername}`;
            } else if (link.type === 'tiktok') {
               let cleanUsername = (link.url || '').replace(/^https?:\/\/(www\.)?tiktok\.com\/(?:@)?/, '').replace(/^https?:\/\//, '').replace(/^@/, '').replace(/\/$/, '');
               finalHref = `https://www.tiktok.com/@${cleanUsername}`;
            }

            const hasValidUrl = finalHref && finalHref.trim() !== '' 
                && finalHref !== 'https://www.instagram.com/' 
                && finalHref !== 'https://www.tiktok.com/@';

            const Wrapper = hasValidUrl ? 'a' : 'div';
            const wrapperProps = hasValidUrl 
              ? { href: finalHref, target: "_blank", rel: "noopener noreferrer", 
                  onClick: () => handleLinkClick(link.type, finalHref) }
              : { style: { opacity: 0.5, cursor: 'not-allowed' }, title: "Enlace no configurado" };

            return (
              <Wrapper
                key={index}
                {...wrapperProps}
                className={`flex items-center gap-2.5 sm:gap-3 p-3 sm:p-4 bg-white rounded-2xl shadow-xl border border-slate-200 transition-all ${hasValidUrl ? 'hover:border-[#feae2c] hover:shadow-md group active:scale-98' : ''}`}
              >
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${customIconObj ? '' : linkConfig.color} flex items-center justify-center flex-shrink-0 overflow-hidden`} style={customIconObj ? { backgroundColor: customIconObj.color } : {}}>
                  {linkConfig.logo ? (
                    <img src={linkConfig.logo} alt={link.label} className={`${linkConfig.imgClass || "w-6 h-6 sm:w-7 sm:h-7"} object-contain`} />
                  ) : (
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  )}
                </div>
                <span className="flex-1 font-medium text-sm sm:text-base text-slate-900 text-left truncate">
                  {link.label}
                </span>
                <ExternalLink className={`w-4 h-4 flex-shrink-0 transition-colors ${hasValidUrl ? 'text-slate-400 group-hover:text-[#feae2c]' : 'text-slate-300'}`} />
              </Wrapper>
            );
          })
        ) : (
          <div className="text-center py-6 sm:py-8 text-slate-400">
            <Link2 className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 opacity-50" />
            <p className="text-xs sm:text-sm">{t('todo_en_1.add_links_to_start')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
